import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Button, Box, Card, CardContent,
  CardActions, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl,
  InputLabel, CircularProgress, Alert, Tooltip, IconButton,
} from '@mui/material';
import SimpleGrid from '../components/SimpleGrid';
import {
  Add, Edit, Delete, Publish, VisibilityOff, School,
  Quiz, BookOutlined,
} from '@mui/icons-material';
import { contentAPI } from '../services/api';
import { FullCourse } from '../types';
import { useNavigate } from 'react-router-dom';

const ContentManagerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<FullCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCourse, setNewCourse] = useState({
    title: '',
    description: '',
    category: 'General',
    difficulty: 'beginner',
    instructor: '',
    thumbnail: '',
  });

  useEffect(() => { loadCourses(); }, []);

  const loadCourses = async () => {
    try {
      setError(null);
      const res = await contentAPI.getCourses();
      setCourses(res.data.courses || []);
    } catch {
      setError('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newCourse.title.trim()) return;
    setCreating(true);
    try {
      const res = await contentAPI.createCourse(newCourse as any);
      setCreateOpen(false);
      setNewCourse({ title: '', description: '', category: 'General', difficulty: 'beginner', instructor: '', thumbnail: '' });
      navigate(`/content/editor/${res.data.course.id}`);
    } catch {
      setError('Failed to create course');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (courseId: string) => {
    if (!window.confirm('Delete this course? This cannot be undone.')) return;
    try {
      await contentAPI.deleteCourse(courseId);
      setCourses(prev => prev.filter(c => c.id !== courseId));
    } catch {
      setError('Failed to delete course');
    }
  };

  const handleTogglePublish = async (courseId: string) => {
    try {
      const res = await contentAPI.publishCourse(courseId);
      setCourses(prev => prev.map(c => c.id === courseId ? res.data.course : c));
    } catch {
      setError('Failed to update course status');
    }
  };

  const stats = {
    total: courses.length,
    published: courses.filter(c => c.is_active).length,
    drafts: courses.filter(c => !c.is_active).length,
    modules: courses.reduce((sum, c) => sum + (c.modules?.length || 0), 0),
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Content Management</Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage courses, modules, and assessments
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} size="large" onClick={() => setCreateOpen(true)}>
          New Course
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Stats */}
      <SimpleGrid container spacing={3} sx={{ mb: 4 }}>
        {[
          { label: 'Total Courses', value: stats.total, color: '#1976d2' },
          { label: 'Published', value: stats.published, color: '#2e7d32' },
          { label: 'Drafts', value: stats.drafts, color: '#ed6c02' },
          { label: 'Total Modules', value: stats.modules, color: '#7b1fa2' },
        ].map(stat => (
          <SimpleGrid item xs={6} md={3} key={stat.label}>
            <Card sx={{ textAlign: 'center', borderTop: `4px solid ${stat.color}` }}>
              <CardContent>
                <Typography variant="h3" fontWeight={700} color={stat.color}>{stat.value}</Typography>
                <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
              </CardContent>
            </Card>
          </SimpleGrid>
        ))}
      </SimpleGrid>

      {/* Course Grid */}
      {courses.length === 0 ? (
        <Box textAlign="center" py={8}>
          <School sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No courses yet</Typography>
          <Typography variant="body2" color="text.disabled" mb={3}>
            Create your first course to get started
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
            Create Course
          </Button>
        </Box>
      ) : (
        <SimpleGrid container spacing={3}>
          {courses.map(course => (
            <SimpleGrid item xs={12} md={6} lg={4} key={course.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', '&:hover': { boxShadow: 4 } }}>
                {/* Thumbnail */}
                <Box
                  sx={{
                    height: 140,
                    background: course.thumbnail
                      ? `url(${course.thumbnail}) center/cover`
                      : 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                    display: 'flex', alignItems: 'flex-end', p: 1,
                  }}
                >
                  <Chip
                    label={course.is_active ? 'Published' : 'Draft'}
                    color={course.is_active ? 'success' : 'default'}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.9)' }}
                  />
                </Box>

                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom noWrap>{course.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {course.description || 'No description'}
                  </Typography>

                  <Box display="flex" gap={1} flexWrap="wrap" mb={1.5}>
                    <Chip label={course.category || 'General'} size="small" variant="outlined" />
                    <Chip label={course.difficulty || 'beginner'} size="small" variant="outlined" color="primary" />
                  </Box>

                  <Box display="flex" gap={2} sx={{ color: 'text.secondary' }}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <BookOutlined fontSize="small" />
                      <Typography variant="caption">{course.modules?.length || 0} modules</Typography>
                    </Box>
                    {course.mock_test && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Quiz fontSize="small" color="warning" />
                        <Typography variant="caption">Mock Test</Typography>
                      </Box>
                    )}
                    {course.final_test && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Quiz fontSize="small" color="success" />
                        <Typography variant="caption">Final Exam</Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>

                <CardActions sx={{ px: 2, pb: 2, gap: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Edit />}
                    onClick={() => navigate(`/content/editor/${course.id}`)}
                    sx={{ flex: 1 }}
                  >
                    Edit
                  </Button>
                  <Tooltip title={course.is_active ? 'Unpublish' : 'Publish'}>
                    <IconButton
                      size="small"
                      color={course.is_active ? 'warning' : 'success'}
                      onClick={() => handleTogglePublish(course.id)}
                    >
                      {course.is_active ? <VisibilityOff /> : <Publish />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => handleDelete(course.id)}>
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </SimpleGrid>
          ))}
        </SimpleGrid>
      )}

      {/* Create Course Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Course</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <TextField
              label="Course Title *"
              fullWidth
              value={newCourse.title}
              onChange={e => setNewCourse(p => ({ ...p, title: e.target.value }))}
              autoFocus
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={newCourse.description}
              onChange={e => setNewCourse(p => ({ ...p, description: e.target.value }))}
            />
            <Box display="flex" gap={2}>
              <TextField
                label="Category"
                fullWidth
                value={newCourse.category}
                onChange={e => setNewCourse(p => ({ ...p, category: e.target.value }))}
              />
              <FormControl fullWidth>
                <InputLabel>Difficulty</InputLabel>
                <Select
                  value={newCourse.difficulty}
                  label="Difficulty"
                  onChange={e => setNewCourse(p => ({ ...p, difficulty: e.target.value }))}
                >
                  <MenuItem value="beginner">Beginner</MenuItem>
                  <MenuItem value="intermediate">Intermediate</MenuItem>
                  <MenuItem value="advanced">Advanced</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="Instructor Name"
              fullWidth
              value={newCourse.instructor}
              onChange={e => setNewCourse(p => ({ ...p, instructor: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!newCourse.title.trim() || creating}
          >
            {creating ? <CircularProgress size={20} /> : 'Create & Edit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ContentManagerDashboard;
